/**
 * Athos Commerce core: tracking cookies and shared request helpers.
 * Loaded synchronously (see snippets/athos-config.liquid) so the cookies exist
 * before any search or autocomplete request is made.
 *
 * Docs: https://docs.athoscommerce.com/docs/getting-started-athos-cookies
 */
(function () {
  const config = window.AthosConfig;
  if (!config || !config.siteId) return;

  const USER_KEY = 'athosUserId';
  const LEGACY_USER_KEY = '_isuid';
  const SESSION_KEY = 'athosSessionId';
  const YEAR = 60 * 60 * 24 * 365;

  const uuid = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const setCookie = (name, value, maxAge) => {
    let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
    if (maxAge) cookie += `; max-age=${maxAge}`;
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  };

  const readSession = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY);
    } catch (e) {
      return null;
    }
  };

  const writeSession = (value) => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, value);
    } catch (e) {
      /* storage blocked: the cookie below still carries the session */
    }
  };

  // _isuid (legacy, deprecated) must always share athosUserId's value.
  const ensureUserId = () => {
    const userId = getCookie(USER_KEY) || getCookie(LEGACY_USER_KEY) || uuid();
    setCookie(USER_KEY, userId, YEAR);
    setCookie(LEGACY_USER_KEY, userId, YEAR);
    return userId;
  };

  // Session is per tab (sessionStorage), mirrored to a session cookie.
  const ensureSessionId = () => {
    let sessionId = readSession();
    if (!sessionId) {
      sessionId = uuid();
      writeSession(sessionId);
    }
    setCookie(SESSION_KEY, sessionId);
    return sessionId;
  };

  // New value for every physical page load (and, via newPageLoad(), every in-page route change).
  let pageLoadId = uuid();

  const newPageLoad = () => {
    const t = tracker();
    pageLoadId = t && t.pageLoad ? t.pageLoad() : uuid();
    return pageLoadId;
  };

  // Beacon.js (window.athos.tracker) owns the ids when it is present, so API requests and tracking
  // events always agree. The cookie code above is only a fallback if the Beacon script is blocked.
  const tracker = () => (window.athos && window.athos.tracker && window.athos.tracker.getContext ? window.athos.tracker : null);
  const hasTracker = !!tracker();
  const userId = hasTracker ? null : ensureUserId();
  const sessionId = hasTracker ? null : ensureSessionId();

  const baseUrl = (siteId) => `https://${siteId}.a.athoscommerce.net`;

  /**
   * Query params required on every Search / Autocomplete / Suggest request.
   * Also enables Beacon 2.0 auto-events; test=true keeps dev traffic out of reporting.
   */
  const trackingParams = () => {
    const t = tracker();
    const context = t ? t.getContext() : null;
    const params = {
      userId: context ? context.userId : getCookie(USER_KEY) || userId,
      sessionId: context ? context.sessionId : readSession() || sessionId,
      pageLoadId: context && context.pageLoadId ? context.pageLoadId : pageLoadId,
      domain: window.location.href,
      beacon: 'true'
    };
    if (config.testMode) params.test = 'true';
    return params;
  };

  /**
   * Build a request URL for an Athos endpoint.
   * @param {string} endpoint e.g. 'search', 'autocomplete', 'suggest'
   * @param {Object} params   endpoint params; null/undefined/'' are skipped, arrays repeat the key
   * @param {Object} [opts]   { siteId } targets another Athos account (blog, collections)
   */
  const buildUrl = (endpoint, params, opts) => {
    const siteId = (opts && opts.siteId) || config.siteId;
    const url = new URL(`${baseUrl(siteId)}/v1/${endpoint}`);
    // API integrations must request JSON; other formats are no longer available.
    const all = Object.assign({ siteId, resultsFormat: 'json' }, params, trackingParams());
    Object.keys(all).forEach((key) => {
      const value = all[key];
      if (Array.isArray(value)) {
        value.forEach((v) => url.searchParams.append(key, v));
      } else if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      }
    });
    return url;
  };

  const request = async (endpoint, params, opts) => {
    const response = await fetch(buildUrl(endpoint, params, opts), {
      signal: opts && opts.signal
    });
    if (!response.ok) throw new Error(`Athos ${endpoint} request failed (${response.status})`);
    return response.json();
  };

  // Extra Athos accounts holding imported site content, in display order.
  const contentSources = [
    { key: 'collections', siteId: config.collectionsSiteId },
    { key: 'blog', siteId: config.contentSiteId }
  ].filter((source) => source.siteId);

  /**
   * Image for a blog article result: its own thumbnail if Athos has one, otherwise its blog's fallback
   * image (see snippets/athos-config.liquid). Most articles have no featured image in Shopify.
   */
  const articleImage = (result) => {
    const own = result.thumbnailImageUrl || result.imageUrl;
    if (own) return own;
    const match = /\/blogs\/([^/]+)\//.exec(result.url || '');
    return (match && (config.blogImages || {})[match[1]]) || '';
  };

  window.Athos = { config, contentSources, getCookie, trackingParams, buildUrl, request, newPageLoad, articleImage };
})();
